import React from "react";
import { useLocation } from "react-router-dom";

import { keyGenerator } from "../../../libs/Util";
import TeacherHeader from "../../../organisms/teacher/TeacherHeader";
import TeachingMaterial from "../../../organisms/TeachingMaterial";
import EducationGoal from "../../../atoms/EducationGoal";
import Button from "../../../atoms/ActiveLearning/Button";
import VideoJS from "../../../atoms/VideoJS";
import StudentLoginQrCodeModal from "../../../molecules/StudentLoginQrCodeModal";
import { chooseTextbookPath, textbookPath } from "../../../teacherStore";
import { ITeachingMaterial } from "../../../state";
import { ReactComponent as IconClose } from "../../../assets/svg/close.svg";
import orientationPDF from "../../../assets/pdf/2025_orien.pdf";
import orientationBg from "../../../assets/images/core_vol0_bg.jpg";
import teachingMaterialStyles from "../../../organisms/TeachingMaterial/TeachingMaterial.module.scss";
import ChatBubbleModal from "../../../atoms/ChatBubbleModal";
import ChatBubblePage from "../../../pages/teacher/ChatBubblePage";
import ChatBubbleIcon from "../../../atoms/ChatBubbleIcon";
import NotifyHintModalPage from "../../../pages/teacher/NotifyHintModalPage";
import {
  CHAT_BUBBLE_MODAL_PAGES,
  ChatBubbleModalPage,
} from "../../../constants/teaching_hint";

import styles from "./SelectPage.module.scss";

export interface ISelectPageProps {
  teaching_materials: ITeachingMaterial[];
  classList: string[];
  educationGoal: string;
  calling: boolean;
  onPublishLesson: (lesson_id: number) => void;
  openGuideline: (teaching_material_id: number, lesson_id: number) => void;
  openMaterial: (teaching_material_id: number, lesson_id: number) => void;
  teacherSurveyEnabled: boolean;
  isSurveyShown: boolean;
  showNotificationHintModal: boolean;
  onCloseNotificationModal: () => void;
}

export const SelectPage = (props: ISelectPageProps) => {
  const links = [
    {
      name: "クラスを選んで指導書を選ぶ",
      to: `${textbookPath}${chooseTextbookPath}`,
    },
  ];

  const [showVideo, setShowVideo] = React.useState(false);
  const [showChat, setShowChat] = React.useState<boolean>(!props.isSurveyShown);
  const [modalPage, setModalPage] = React.useState<ChatBubbleModalPage>(
    CHAT_BUBBLE_MODAL_PAGES.SURVEY,
  );
  const [videoOptions, setVideoOptions] = React.useState({
    url: `${process.env.REACT_APP_TEACHER_DLP_URL}/movie/CORE2.3/orientation_core_2025/orientation_core_2025`,
    playing: false,
  });
  const [
    showStudentLoginQrCodeModal,
    setShowStudentLoginQrCodeModal,
  ] = React.useState(false);

  const { pathname } = useLocation();

  React.useEffect(() => {
    setModalPage(
      pathname.includes("guideline")
        ? CHAT_BUBBLE_MODAL_PAGES.HINTS
        : CHAT_BUBBLE_MODAL_PAGES.SURVEY,
    );
  }, [pathname, setModalPage]);

  const handleOpenVideo = () => {
    setShowVideo(true);
    setVideoOptions({
      url: videoOptions.url,
      playing: false,
    });
  };

  const handleCloseVideo = () => {
    setVideoOptions(prevState => ({
      ...prevState,
      playing: false,
    }));
    setShowVideo(false);
  };

  const handleCloseHintModal = () => {
    props.onCloseNotificationModal();
  };

  return (
    <div className={styles.wrapper} id="accordion">
      <div
        className={styles.video}
        style={{
          display: showVideo && videoOptions.url.length > 0 ? "block" : "none",
        }}
      >
        <div className={styles.close} onClick={handleCloseVideo}>
          <IconClose />
        </div>
        {!!videoOptions.url && (
          <VideoJS
            key={videoOptions.url}
            className={styles.videojs}
            playing={videoOptions.playing}
            showVideo={showVideo}
            sourceUrl={videoOptions.url}
          />
        )}
      </div>
      {showStudentLoginQrCodeModal && (
        <StudentLoginQrCodeModal
          onClose={() => setShowStudentLoginQrCodeModal(false)}
        />
      )}
      <TeacherHeader links={links}>
        <div className={styles.classList}>
          <span>選択中のクラス：</span>
          <p className={styles.class}>
            {props.classList.map(item => (
              <span className="ms-2" key={keyGenerator(item)}>
                {item}
              </span>
            ))}
          </p>
        </div>
      </TeacherHeader>
      {props.educationGoal && (
        <div className={styles.educationGoal}>
          <EducationGoal content={props.educationGoal} />
        </div>
      )}
      <div className={styles.studentQrCode}>
        <Button
          variant={"primary"}
          width={"340px"}
          height={"37px"}
          fontSize={"14px"}
          handleClick={() => setShowStudentLoginQrCodeModal(true)}
        >
          生徒がURLやパスワードを忘れた場合はこちら
        </Button>
      </div>
      {props.teaching_materials.some(tm =>
        tm.title.includes("オリエンテーション"),
      ) && (
        <div className={teachingMaterialStyles.container}>
          <div
            className={teachingMaterialStyles.cover}
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3),
        rgba(0, 0, 0, 0.3)), url(${orientationBg})`,
            }}
            data-bs-toggle="collapse"
            data-bs-target={`#orientation`}
            role="button"
            aria-expanded="false"
            aria-controls={`#orientation`}
          >
            <div className={teachingMaterialStyles.header}>
              <div className={teachingMaterialStyles.name} />
              <div className={teachingMaterialStyles.title}>
                オリエンテーション
              </div>
            </div>
          </div>
          <div
            className={`collapse ${teachingMaterialStyles.collapseOrientation}`}
            id={`orientation`}
            data-bs-parent="#accordion"
          >
            <div className={teachingMaterialStyles.wrapper}>
              <p className={teachingMaterialStyles.label}>
                ENAGEEDについて/ログイン/教材の操作
              </p>
              <div className={teachingMaterialStyles.btnsArea}>
                <Button
                  variant={"primary"}
                  width={"130px"}
                  height={"37px"}
                  fontSize={"14px"}
                  handleClick={handleOpenVideo}
                >
                  動画を再生する
                </Button>
                <a
                  href={orientationPDF}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    variant={"secondary"}
                    width={"88px"}
                    height={"37px"}
                    fontSize={"14px"}
                  >
                    投影画面
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      {props.teaching_materials.length > 0 &&
        props.teaching_materials
          .filter(material => !material.title.includes("オリエンテーション")) // 2023年度のオリエンを非表示にするための暫定対処
          .map(material => (
            <TeachingMaterial
              key={material.id}
              teaching_material={material}
              onPublishLesson={props.onPublishLesson}
              calling={props.calling}
              openGuideline={props.openGuideline}
              openMaterial={props.openMaterial}
            />
          ))}

      {props.teacherSurveyEnabled && (
        <>
          <ChatBubbleIcon
            onClose={() => setShowChat(!showChat)}
            modalPage={modalPage}
          />
          {showChat && (
            <ChatBubbleModal
              onClose={() => {
                setShowChat(false);
                setModalPage(CHAT_BUBBLE_MODAL_PAGES.SURVEY);
              }}
              transparentBackDrop={true}
            >
              <ChatBubblePage
                onClose={() => {
                  setShowChat(false);
                  setModalPage(CHAT_BUBBLE_MODAL_PAGES.SURVEY);
                }}
                modalPage={modalPage}
                setModalPage={setModalPage}
              />
            </ChatBubbleModal>
          )}

          {props.showNotificationHintModal && (
            <NotifyHintModalPage onClose={handleCloseHintModal} />
          )}
        </>
      )}
    </div>
  );
};

export default SelectPage;
